/**
 * Modal de criar/editar contato — C-75 + GUPM-202 (só admin; o banco é o gate real).
 *
 * Valida no domínio antes de enviar: com erro, nada vai ao banco nem o popup de
 * confirmação abre. A recusa do servidor (permissão, limite, vínculo duplicado,
 * rede) aparece no próprio modal. Estado e regras em `useContatoForm`.
 *
 * Ajuste de 14/10 (Gabriel):
 * - rodapé: Desativar/Ativar à esquerda; Cancelar e "Salvar alterações" à direita,
 *   este desabilitado enquanto nada mudar (`houveAlteracao`);
 * - criar, salvar e desativar passam pelo popup "Sim/Não" (`ConfirmacaoContato`); "Não"
 *   volta ao modal com o digitado;
 * - desativar e desmarcar a emergência respeitam o mínimo de 2 de emergência.
 */
import { useContatoForm } from '@/hooks/useContatoForm';
import { usePopup } from '@/hooks/usePopup';
import { useFecharComConfirmacao } from '@/hooks/useFecharComConfirmacao';
import { ConfirmarDescarte } from '@/components/ConfirmarDescarte';
import type { ContatoGestao } from '@/services/datasources';
import CamposContato from './CamposContato';
import ConfirmacaoContato from './ConfirmacaoContato';
import RodapeContato from './RodapeContato';

interface Props {
  /** `null` = contato novo. */
  contato: ContatoGestao | null;
  /** Todos os contatos (ativos e inativos, sem repetição): ordem, travas, sugestões e vínculos. */
  todos: readonly ContatoGestao[];
  /** Sugestões para o campo Empresa. */
  empresas: readonly string[];
  onClose: () => void;
}

export default function ContatoForm({ contato, todos, empresas, onClose }: Props) {
  const form = useContatoForm(contato, todos, onClose);
  const { confirmacao, setConfirmacao, ocupado, erro } = form;
  // C-91: ✕, Esc e Cancelar com algo preenchido perguntam antes de descartar; o Esc
  // trava enquanto grava, como o ✕. Com um popup por cima, o Esc é dele (`usePopup`).
  const saida = useFecharComConfirmacao({ preenchido: form.preenchido, ocupado, fechar: onClose });
  const popupRef = usePopup<HTMLDivElement>({ aoFechar: saida.pedirFechar, bloqueado: ocupado });

  const avisoDesativar = contato?.is_emergencia
    ? 'Ele sai da lista de contatos de emergência de todos os usuários. Dá para ativar de novo depois.'
    : 'Ele sai da lista de contatos. Dá para ativar de novo depois.';

  return (
    <div
      ref={popupRef}
      tabIndex={-1}
      className="fixed inset-0 z-[2000] flex items-start justify-center overflow-y-auto bg-black/60 p-4 focus:outline-none"
      role="dialog"
      aria-modal="true"
      aria-labelledby="contato-form-titulo"
    >
      <form onSubmit={form.enviar} noValidate className="mt-10 w-full max-w-md rounded-xl border border-jf-border bg-jf-surface p-4">
        <div className="mb-3 flex items-center justify-between">
          <h2 id="contato-form-titulo" className="text-sm font-bold text-white">
            {contato ? 'Editar contato' : 'Novo contato'}
          </h2>
          <button type="button" onClick={saida.pedirFechar} disabled={ocupado} className="text-sm font-bold text-jf-muted hover:text-white disabled:opacity-50" aria-label="Fechar">
            ✕
          </button>
        </div>

        <CamposContato form={form} empresas={empresas} />

        {erro && (
          <p role="alert" className="mt-3 rounded-md border border-red-500/40 bg-red-500/10 px-2 py-1.5 text-xs text-red-300">
            {erro instanceof Error ? erro.message : 'Não foi possível salvar o contato.'}
          </p>
        )}

        <RodapeContato
          contato={contato}
          bloqueio={form.bloqueioDesativar}
          ocupado={ocupado}
          mudou={form.mudou}
          salvando={form.salvando}
          pendente={contato !== null && form.mudou}
          onDesativar={() => setConfirmacao('desativar')}
          onAtivar={() => form.definirAtivo(true)}
          onCancelar={saida.pedirFechar}
        />
      </form>

      {confirmacao === 'salvar' && (
        <ConfirmacaoContato
          titulo={contato ? 'Confirmar alteração?' : 'Cadastrar contato?'}
          texto={
            contato
              ? 'As alterações valem na hora para todos os usuários.'
              : 'O contato passa a valer na hora para todos os usuários.'
          }
          onSim={form.confirmarSalvar}
          onNao={() => setConfirmacao(null)}
        />
      )}
      {confirmacao === 'desativar' && (
        <ConfirmacaoContato
          titulo="Desativar contato?"
          texto={avisoDesativar}
          onSim={() => form.definirAtivo(false)}
          onNao={() => setConfirmacao(null)}
        />
      )}
      {saida.perguntando && <ConfirmarDescarte onContinuar={saida.continuar} onDescartar={saida.descartar} />}
    </div>
  );
}
